import { Center, ChakraProvider, Container, useColorMode } from '@chakra-ui/react';
import * as React from 'react';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import { ErrorBoundary } from 'react-error-boundary';
import { QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import './App.css';
import ErrorBoundaryHome from './components/errors/ErrorBoundaryHome';
import i18n from './i18n/i18n';
import { normalizeLocale } from './i18n/loadNamespace';
import Router from './router/Router';
import customTheme from './theme';
import { getLanguage } from '@pocapan/shared/utils/etcUtils';
import { createQueryClient } from '@pocapan/shared/utils/queryClient';

const queryClient = createQueryClient();
const styledLightTheme = { isDark: false };
const styledDarkTheme = { isDark: true };

function ThemeInitializer() {
  const { setColorMode } = useColorMode();

  React.useEffect(() => {
    const prefersDarkMode = window.matchMedia('(prefers-color-scheme: dark)');

    function handlePrefersColorSchemeChange(event) {
      const systemTheme = event.matches ? 'dark' : 'light';
      setColorMode(systemTheme);
      localStorage.setItem('chakra-ui-color-mode', systemTheme);
      document.documentElement.classList.toggle('dark', systemTheme === 'dark');
    }

    handlePrefersColorSchemeChange(prefersDarkMode);
    prefersDarkMode.addEventListener('change', handlePrefersColorSchemeChange);

    return () => {
      prefersDarkMode.removeEventListener('change', handlePrefersColorSchemeChange);
    };
  }, [setColorMode]);

  return null;
}

function App() {
  const { colorMode } = useColorMode();

  React.useEffect(() => {
    const savedLanguage = getLanguage();
    if (savedLanguage) {
      const normalizedLanguage = normalizeLocale(savedLanguage);
      if (normalizedLanguage !== i18n.language) {
        i18n.changeLanguage(normalizedLanguage);
      }
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary fallback={<ErrorBoundaryHome />}>
        <ChakraProvider theme={customTheme}>
          <ThemeInitializer />
          <StyledThemeProvider theme={colorMode === 'dark' ? styledDarkTheme : styledLightTheme}>
            <Center
              padding={0}
              backgroundSize={{ xl: 'cover' }}
              bg={colorMode === 'dark' ? 'background.dark' : 'background.light'}
            >
              <div className="bg-background-light dark:bg-background-dark w-full min-h-screen h-full">
                <Container
                  width={'100%'}
                  padding={0}
                  bg={colorMode === 'dark' ? 'background.dark' : 'background.light'}
                  boxShadow={{
                    xl: `0px 0px 24px 0px ${
                      colorMode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.1)'
                    }`
                  }}
                >
                  <Router />
                  {/* <ReactQueryDevtools initialOpen={false} /> */}
                </Container>
              </div>
            </Center>
          </StyledThemeProvider>
        </ChakraProvider>
      </ErrorBoundary>
    </QueryClientProvider>
  );
}

export default App;
